import React from 'react';
import PropTypes from 'prop-types';
import uniqid from 'uniqid';
import _ from 'lodash';
import { Box, Text } from 'grommet';
import { FormView } from 'grommet-icons';
import { useAppTheme } from '@Components/Context';
import { SimpleModal } from '@Components/Control';
import {
  StyledModalActivateButton,
  StyledModalSelectButton,
} from './StyledProductFormComponent';


const ModalSelect = ({
  small,
  handleFormValues,
  value = null,
  options = null,
  objectOptions = null,
  selectionKey = null,
  displayKey = null,
  description = null,
  fieldTitle,
  inputConfig = null,
  searchTypeKey,
  platformOptionOverrides = null,
  ...rest
}) => {
  const [showModal, setShowModal] = React.useState(false);
  const [selectedValues, setSelectedValues] = React.useState([]);
  const { theme: { page, button } } = useAppTheme();
  let selectableValues = options;

  const handleSelection = (uid) => {
    const prevSelected = [...selectedValues];
    if (prevSelected.includes(uid)) {
      _.pull(prevSelected, uid);
    } else {
      prevSelected.push(uid);
    }

    setSelectedValues(prevSelected);
  };

  let displayValues = value && value.join(', ');

  if (objectOptions) {
    selectableValues = objectOptions;

    displayValues = value && value.map((val) => {
      const obj = objectOptions?.find((dt) => dt?.[selectionKey] === val);
      return obj?.[displayKey];
    }).join(', ');
  }

  if (value?.length > 4) {
    displayValues = `${value.slice(0, 4).join(', ')} + ${value.length - 4} more`;

    if (objectOptions) {
      displayValues = `${objectOptions
        .filter((dt) => value.includes(dt[selectionKey]))
        .slice(0, 4)
        .map((dt) => dt[displayKey])
        .join(', ')} + ${value.length - 4} more`;
    }
  }

  if (platformOptionOverrides?.length > 0) {
    selectableValues = platformOptionOverrides;
  } else if (inputConfig?.supportedCategoryValues) {
    selectableValues = inputConfig.supportedCategoryValues[searchTypeKey];
  }

  const allowSelection = selectedValues?.length >= 1 || value?.length >= 1;

  const renderSelectableOptions = () => {
    if (!selectableValues || selectableValues?.length === 0) {
      return (
        <Box width="100%" pad={{ vertical: '1rem' }} align="center">
          <Text size="1rem" color={page.modalText}>
            No options available
          </Text>
        </Box>
      );
    }

    if (objectOptions && selectionKey && displayKey) {
      return (
        <Box wrap direction="row">
          {selectableValues?.map((dt) => (
            <Box key={uniqid()} pad={{ right: '1rem', vertical: '0.5rem' }}>
              <StyledModalSelectButton
                plain
                selected={selectedValues.includes(dt[selectionKey])}
                textSize="1rem"
                onClick={() => handleSelection(dt[selectionKey])}
                bgColor={button.tableBg}
                primaryText={page.modalText}
                buttonHighlight={button.primaryText}
                buttonBg={button.hoverBg}
                label={dt[displayKey]}
              />
            </Box>
          ))}
        </Box>
      );
    }

    return (
      <Box wrap direction="row">
        {selectableValues?.map((dt) => (
          <Box key={uniqid()} pad={{ right: '1rem', vertical: '0.5rem' }}>
            <StyledModalSelectButton
              plain
              selected={selectedValues.includes(dt)}
              textSize="1rem"
              onClick={() => handleSelection(dt)}
              bgColor={button.tableBg}
              primaryText={page.modalText}
              buttonHighlight={button.primaryText}
              buttonBg={button.hoverBg}
              label={dt}
            />
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box>
      <StyledModalActivateButton
        plain
        lessPad
        textSize="1rem"
        onClick={() => setShowModal(true)}
        modalOpen={showModal}
        highlight={page.focusHighlight}
        borderColor={page.inputBorder}
        label={(
          <Box direction="row" justify="between" align="center">
            <Text size="0.8rem" color={page.primaryText}>
              {displayValues || 'Select options'}
            </Text>
            <FormView color={button.iconColor} size="1.3rem" />
          </Box>
        )}
        {...rest}
      />
      {showModal && (
        <SimpleModal
          small={small}
          toggleModal={(val) => {
            if (allowSelection) handleFormValues(selectedValues);
            setShowModal(val);
          }}
          modalTitle={fieldTitle}
          textColor={page.modalText}
          highlightColor={page.highlight}
          modalBg={page.modalBg}
          titleSize="1.1rem"
          desktopMinWidth="50rem"
        >
          {description && (
            <Box width="100%">
              <Text size={small ? '0.85rem' : '0.75rem'} color={page.modalText}>
                {description}
              </Text>
            </Box>
          )}
          {renderSelectableOptions()}
        </SimpleModal>
      )}
    </Box>
  );
};

ModalSelect.propTypes = {
  small: PropTypes.bool.isRequired,
  inputConfig: PropTypes.shape({
    supportedCategoryValues: PropTypes.objectOf(PropTypes.any),
  }),
  searchTypeKey: PropTypes.string.isRequired,
  handleFormValues: PropTypes.func.isRequired,
  fieldTitle: PropTypes.string.isRequired,
  value: PropTypes.arrayOf(PropTypes.string.isRequired),
  options: PropTypes.arrayOf(PropTypes.string.isRequired),
  objectOptions: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  selectionKey: PropTypes.string,
  displayKey: PropTypes.string,
  description: PropTypes.string,
  platformOptionOverrides: PropTypes.arrayOf(PropTypes.string.isRequired),
};

export default ModalSelect;
